import React, { useState } from 'react';

import { useFlowState } from '@betterment/js-runtime';
import { Box, Button, CenteredLayout, FormikField, H1, Input, Label, RadioGroup, Text } from '@betterment/samba-react';
import { FormikValidateAfterSubmit } from '@betterment/shared-components';
import { Form } from 'formik';
import * as Yup from 'yup';

import CashReserveFirstFeeAssessmentExplanationModal from './cash-reserve-first-fee-assessment-explanation-modal/CashReserveFirstFeeAssessmentExplanationModal';
import { BillingPlanFlowValues, BillingPlanStepProps } from './data';
import { useBillingPlanStorage } from './hooks/useBillingPlansStorage';
import { getFlowTitle } from './utils';

import { BillingRate, PaymentType as PaymentTypeGraphQl } from '../../../graphql/schema';
import FlowStepLayout from '../../BettermentForAdvisors/FlowStepLayout';

const Schema = Yup.object().shape({
  planName: Yup.string().required('Plan name is required').max(255, 'Plan name must be 255 characters or less'),
  configurationType: Yup.string<BillingRate['__typename']>().required('Configuration type is required'),
  paymentType: Yup.string<PaymentTypeGraphQl>().required('Payment type is required'),
});

interface HeaderProps {
  isEdit?: boolean;
}

const Header = ({ isEdit }: HeaderProps) => (
  <Box>
    <H1 css={{ pb: 'space.200' }}>{isEdit ? 'Edit' : 'Create'} billing plan.</H1>
    <Text>
      Plans apply to invested assets only. Clients are charged on balances only after trading completes and assets are
      invested.
    </Text>
  </Box>
);

const PlanName = () => (
  <Box>
    <FormikField id="planName" name="planName">
      <FormikField.Label>Plan name</FormikField.Label>
      <FormikField.Control>
        <Input />
      </FormikField.Control>
      <FormikField.ErrorMessage />
    </FormikField>
  </Box>
);

const ConfigType = () => (
  <Box>
    <FormikField name="configurationType" id="configurationType">
      <FormikField.Control>
        <RadioGroup aria-labelledby="configurationTypeGroupLabel" css={{ gap: '0' }}>
          <Label id="configurationTypeGroupLabel" css={{ pb: 'space.50' }}>
            What type of billing plan would you like to use?
          </Label>
          <RadioGroup.ItemContainer css={{ pt: 'space.200' }}>
            <RadioGroup.Item value="BillingRateAssetBased" />
            <RadioGroup.ItemLabel>Asset based</RadioGroup.ItemLabel>
          </RadioGroup.ItemContainer>
          <Text variant="text50" color="text.weak" css={{ pt: 'space.50', pl: '28px' }}>
            Charge a flat rate, e.g. 75 bps.
          </Text>
          <RadioGroup.ItemContainer css={{ pt: 'space.150' }}>
            <RadioGroup.Item value="BillingRateFixed" />
            <RadioGroup.ItemLabel>Fixed</RadioGroup.ItemLabel>
          </RadioGroup.ItemContainer>
          <Text variant="text50" color="text.weak" css={{ pt: 'space.50', pl: '28px' }}>
            Charge a fixed amount, e.g. $5,000.
          </Text>
          <RadioGroup.ItemContainer css={{ pt: 'space.150' }}>
            <RadioGroup.Item value="BillingRateTiered" />
            <RadioGroup.ItemLabel>Tiered</RadioGroup.ItemLabel>
          </RadioGroup.ItemContainer>
          <Text variant="text50" color="text.weak" css={{ pt: 'space.50', pl: '28px' }}>
            Charge different amounts based on quantity of assets.
          </Text>
        </RadioGroup>
      </FormikField.Control>
      <FormikField.ErrorMessage />
    </FormikField>
  </Box>
);

const PaymentTypeCashReserveFirst = () => {
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  return (
    <Box css={{ display: 'flex', alignItems: 'center' }}>
      <FormikField name="paymentType" id="paymentType">
        <FormikField.Control>
          <RadioGroup aria-labelledby="paymentTypeGroupLabel" css={{ gap: '0' }}>
            <Label id="paymentTypeGroupLabel" css={{ pb: 'space.50' }}>
              How should advisory fees be billed?
            </Label>
            <RadioGroup.ItemContainer css={{ pt: 'space.200' }}>
              <RadioGroup.Item value={PaymentTypeGraphQl.CashReserveFirst} />
              <RadioGroup.ItemLabel>Cash Reserve first</RadioGroup.ItemLabel>
            </RadioGroup.ItemContainer>
            <Box
              css={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Text variant="text50" color="text.weak" css={{ pt: 'space.50', pl: '28px' }}>
                We'll bill fees in this order:
              </Text>
              <Text variant="text50" color="text.weak">
                <ol style={{ margin: 0 }}>
                  <li>
                    Cash Reserve accounts (starting with the largest balance,{' '}
                    <Button variant="textPrimary" onClick={() => setModalOpen(true)}>
                      {' '}
                      subject to restrictions{' '}
                    </Button>
                    )
                  </li>
                  <li>Taxable investing accounts (starting with the largest balance).</li>
                  <li>Tax-advantaged accounts where fees accrued.</li>
                </ol>
              </Text>
              <CashReserveFirstFeeAssessmentExplanationModal
                isOpen={isModalOpen}
                closeHandler={() => setModalOpen(false)}
              />
            </Box>
            <RadioGroup.ItemContainer css={{ pt: 'space.200' }}>
              <RadioGroup.Item value={PaymentTypeGraphQl.SubAccount} />
              <RadioGroup.ItemLabel>Investing</RadioGroup.ItemLabel>
            </RadioGroup.ItemContainer>
            <Text variant="text50" color="text.weak" css={{ pt: 'space.50', pl: '28px' }}>
              Fees will be billed from the investing accounts where they were accrued.
            </Text>
          </RadioGroup>
        </FormikField.Control>
        <FormikField.ErrorMessage />
      </FormikField>
    </Box>
  );
};

export const ConfigurationSelectionStep: React.FC<BillingPlanStepProps> = ({ goToNextStep }) => {
  const {
    flowState: { values: flowValues },
  } = useFlowState<BillingPlanFlowValues>();
  const { saveValues } = useBillingPlanStorage();
  const handleSubmit = (formValues: BillingPlanFlowValues) => {
    saveValues({ ...formValues });
    goToNextStep({ ...formValues });
  };

  return (
    <FlowStepLayout exitToPath={flowValues.exitToPath} title={getFlowTitle(flowValues, 1)}>
      <CenteredLayout size="small" css={{ maxWidth: '480px' }}>
        <Box css={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 'space.400' }}>
          <Header isEdit={!!flowValues.billingPlanId} />
          <FormikValidateAfterSubmit
            initialValues={{ ...flowValues }}
            onSubmit={handleSubmit}
            validationSchema={Schema}
          >
            {() => (
              <Form>
                <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.400' }}>
                  <PlanName />
                  <ConfigType />
                  <PaymentTypeCashReserveFirst />
                  <Box>
                    <Button type="submit">Continue</Button>
                  </Box>
                </Box>
              </Form>
            )}
          </FormikValidateAfterSubmit>
        </Box>
      </CenteredLayout>
    </FlowStepLayout>
  );
};
